<script lang="ts">
  import { api } from '$shared/api';
  import { CREATOR_TRACK_SUBMISSION } from '$lib/features';
  import LoadingSkeleton from '$shared/LoadingSkeleton.svelte';
  import PlayerEmbed from '$components/shared/PlayerEmbed.svelte';
  import HeartButton from '$components/shared/HeartButton.svelte';
  import Avatar from '$shared/Avatar.svelte';
  import type { Track } from '$types/index';

  let { id }: { id: string } = $props();
  // One unified `track` entity (Track Unification). GET /tracks/{id} serves ANY track id:
  // the catalog header (title/artist/album/style/views + is_favorited) is ALWAYS present,
  // and the duel context (submitter, votes, duel, AI score) is present only when the track
  // was submitted to a duel. `hasDuel` (duel_id != null) chooses which view to render.
  let track = $state<Track | null>(null);
  let loading = $state(true);
  let error = $state(false);

  interface DnaEntry {
    genre: string;
    pct: number;
  }
  interface TrackLegacy {
    baseline_views: number;
    current_views: number;
    delta_views: number;
    baseline_likes: number;
    current_likes: number;
    delta_likes: number;
    submitted_at: string;
    days_since: number;
    track_type: string;
  }
  let voterDna = $state<DnaEntry[]>([]);
  let voterCount = $state(0);
  let legacy = $state<TrackLegacy | null>(null);

  interface SimilarTrack {
    id: number;
    title: string | null;
    artist: string | null;
    image_url: string | null;
  }
  let similar = $state<SimilarTrack[]>([]);

  const hasDuel = $derived(track != null && track.duel_id != null);

  // GET /tracks/{id} returns the flat unified Track (catalog header always + duel context
  // when submitted). Shared by the initial load and the "Try again" button so a retry also
  // re-fetches the voter-DNA / legacy follow-ups.
  function load() {
    error = false;
    loading = true;
    track = null;
    voterDna = [];
    voterCount = 0;
    legacy = null;
    similar = [];

    api
      .get<Track>(`/tracks/${id}`)
      .then((t) => {
        track = t;
        loading = false;
        // Similar tracks — lazy-fetch for all track types (catalog + duel).
        api
          .get<{ similar: SimilarTrack[] }>(`/tracks/${id}/similar`)
          .then((r) => {
            similar = r.similar ?? [];
          })
          .catch((e) => console.warn('Failed to load similar tracks:', e));
        // Voter DNA + legacy only exist for a track that was submitted to a duel.
        if (t && t.duel_id != null) {
          api
            .get<{ voter_dna: DnaEntry[]; voter_count: number }>(`/tracks/${id}/voter-dna`)
            .then((dna) => {
              voterDna = dna.voter_dna ?? [];
              voterCount = dna.voter_count ?? 0;
            })
            .catch((e) => console.warn('Failed to load voter DNA:', e));
          api
            .get<{ legacy: TrackLegacy | null }>(`/tracks/${id}/legacy`)
            .then((lg) => {
              legacy = lg.legacy ?? null;
            })
            .catch((e) => console.warn('Failed to load track legacy:', e));
        }
      })
      .catch((e) => {
        console.error('Failed to load track:', e);
        error = true;
        loading = false;
      });
  }

  $effect(() => {
    void id; // re-load when the route param changes
    load();
  });

  function formatCount(n: number | null): string {
    if (n == null) return '';
    if (n >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
    if (n >= 1_000) return (n / 1_000).toFixed(n >= 10_000 ? 0 : 1).replace(/\.0$/, '') + 'K';
    return String(n);
  }
  function formatDelta(n: number): string {
    if (n >= 1_000_000) return '+' + (n / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
    if (n >= 1_000) return '+' + (n / 1_000).toFixed(n >= 10_000 ? 0 : 1).replace(/\.0$/, '') + 'K';
    return '+' + String(n);
  }

  const maxPct = $derived(voterDna.length > 0 ? voterDna[0].pct : 100);
</script>

<div class="narrow">
  {#if loading}
    <LoadingSkeleton lines={5} />
  {:else if track}
    <PlayerEmbed
      trackType={track.track_type}
      youtubeId={track.youtube_id}
      soundcloudUrl={track.soundcloud_url}
      tiktokUrl={track.tiktok_url}
      uploadUrl={track.upload_url}
      uploadImageUrl={track.upload_image_url}
    />

    <div class="track-detail">
      <div class="title-row">
        <h1>{track.track_name || 'Untitled'}</h1>
        <HeartButton trackId={Number(id)} initialFavorited={track.is_favorited} />
      </div>

      {#if track.vibe_match_pct != null}
        <div class="tag-pills">
          <span class="tag-pill">🎧 {track.vibe_match_pct}% your vibe</span>
        </div>
      {/if}

      {#if hasDuel}
        <!-- Duel-submission context -->
        <div class="track-attribution">
          <a href="#/users/{track.username}" class="submitter-link">
            <Avatar username={track.username} avatarUrl={track.avatar_url} size="xs" />
            {track.username || 'Unknown'}
          </a>
          {#if track.artist_name && track.artist_slug}
            <span class="attr-sep">&middot;</span>
            <a href="#/artists/{track.artist_slug}" class="artist-link">{track.artist_name}</a>
          {/if}
        </div>
        <div class="stats">
          {track.vote_count ?? 0} votes &middot; {track.listen_count ?? 0} listens
          {#if track.lb_listen_count}
            &middot; {track.lb_listen_count.toLocaleString()} worldwide listens
          {/if}
          {#if track.sc_play_count}
            &middot; {track.sc_play_count.toLocaleString()} SC plays{/if}
          {#if track.ai_score != null}
            &middot; <a href="#/tracks/{track.track_id || (track as any).id}/ai-score" class="ai-link"
              >AI Score: {track.ai_score}/100</a
            >
          {/if}
        </div>
        {#if track.lb_genre_tags && track.lb_genre_tags.length > 0}
          <div class="tag-pills">
            {#each track.lb_genre_tags.slice(0, 6) as tag}
              <span class="tag-pill">{tag}</span>
            {/each}
          </div>
        {/if}

        {#if (track as any).recent_voters && (track as any).recent_voters.length > 0}
          <div class="voted-with">
            <div class="voted-with-avatars">
              {#each (track as any).recent_voters.slice(0, 5) as v}
                <a href="#/users/{v.username}" class="voted-with-avatar" title="@{v.username}">
                  <Avatar username={v.username} avatarUrl={v.avatar_url} size="xs" />
                </a>
              {/each}
            </div>
            <span class="voted-with-text">
              {#if (track as any).real_vote_count > (track as any).recent_voters.length}
                {(track as any).recent_voters[0].username} and
                {((track as any).real_vote_count - 1).toLocaleString()} other{(track as any).real_vote_count - 1 === 1
                  ? ''
                  : 's'} voted
              {:else if (track as any).recent_voters.length === 1}
                {(track as any).recent_voters[0].username} voted
              {:else}
                {(track as any).recent_voters[0].username} and
                {(track as any).recent_voters.length - 1} other{(track as any).recent_voters.length - 1 === 1
                  ? ''
                  : 's'} voted
              {/if}
            </span>
          </div>
        {/if}

        {#if (track as any).duel_name}
          <div class="duel-context">
            <a
              href={`#/duels/${track.duel_id}${(track as any).duel_status === 'ended' ? '/result' : ''}`}
              class="duel-link"
            >
              {(track as any).duel_name}
            </a>
            {#if (track as any).duel_status === 'ended' && (track as any).duel_rank}
              <span
                class="duel-rank"
                class:rank-1={(track as any).duel_rank === 1}
                class:rank-2={(track as any).duel_rank === 2}
                class:rank-3={(track as any).duel_rank === 3}
              >
                #{(track as any).duel_rank} of {(track as any).duel_track_count}
              </span>
            {:else if (track as any).duel_status === 'active'}
              <span class="duel-status-badge">Live</span>
            {/if}
          </div>
        {/if}

        <div class="submit-cta">
          {#if CREATOR_TRACK_SUBMISSION}
            <a href="#/submit-guide" class="submit-link">Want to submit a track to a duel?</a>
          {/if}
          <a href="#/duels" class="browse-link">Browse active duels</a>
        </div>

        {#if voterDna.length > 0}
          <div class="voter-dna">
            <h3 class="voter-dna-title">Voter Audience DNA</h3>
            <p class="voter-dna-sub">{voterCount} connected voter{voterCount !== 1 ? 's' : ''}</p>
            <div class="dna-bars">
              {#each voterDna as entry}
                <div class="dna-row">
                  <span class="dna-label">{entry.genre}</span>
                  <div class="dna-bar-bg">
                    <div class="dna-bar-fill" style="width: {(entry.pct / maxPct) * 100}%"></div>
                  </div>
                  <span class="dna-pct">{entry.pct}%</span>
                </div>
              {/each}
            </div>
          </div>
        {/if}

        {#if legacy && legacy.delta_views > 0}
          <div class="legacy-section">
            <h3 class="legacy-title">The Beat Duel Effect</h3>
            <div class="legacy-stats">
              <div class="legacy-stat">
                <span class="legacy-delta">{formatDelta(legacy.delta_views)}</span>
                <span class="legacy-label"
                  >{legacy.track_type === 'soundcloud' ? 'plays' : 'views'} since submitted</span
                >
              </div>
              {#if legacy.delta_likes > 0}
                <div class="legacy-stat">
                  <span class="legacy-delta">{formatDelta(legacy.delta_likes)}</span>
                  <span class="legacy-label">likes since submitted</span>
                </div>
              {/if}
            </div>
            <span class="legacy-sub">{legacy.days_since} day{legacy.days_since !== 1 ? 's' : ''} ago</span>
          </div>
        {/if}
      {:else}
        <!-- Catalog-only track (never submitted to a duel) -->
        <div class="attribution">
          {#if track.artist_name}
            <span class="artist">{track.artist_name}</span>
          {/if}
          {#if track.album_name}
            <span class="sep">&middot;</span>
            <span class="album">{track.album_name}</span>
          {/if}
          <span class="source-badge">Catalog track</span>
        </div>

        <div class="stats">
          {#if track.yt_view_count}
            {formatCount(track.yt_view_count)} YouTube views
          {/if}
          {#if track.lb_listen_count}
            {#if track.yt_view_count}&middot;{/if}
            {formatCount(track.lb_listen_count)} worldwide listens
          {/if}
          {#if track.lb_popularity_tier && track.lb_popularity_tier !== 'unknown'}
            {#if track.yt_view_count || track.lb_listen_count}&middot;{/if}
            <span class="tier tier-{track.lb_popularity_tier}">{track.lb_popularity_tier}</span>
          {/if}
        </div>

        {#if track.style || (track.genre_tags && track.genre_tags.length > 0)}
          <div class="tag-pills">
            {#if track.style}
              <span class="tag-pill tag-style">{track.style}</span>
            {/if}
            {#if track.genre_tags}
              {#each track.genre_tags.slice(0, 6) as tag}
                <span class="tag-pill">{tag}</span>
              {/each}
            {/if}
          </div>
        {/if}

        <div class="back">
          <a href="#/duels">&larr; Back to Duels</a>
        </div>
      {/if}

      {#if similar.length > 0}
        <div class="similar-rail">
          <h3 class="similar-rail__title">Sounds like this</h3>
          <div class="similar-rail__track-row">
            {#each similar as s (s.id)}
              <a class="similar-card" href="#/tracks/{s.id}">
                {#if s.image_url}
                  <img class="similar-card__art" src={s.image_url} alt="" loading="lazy" />
                {/if}
                <div class="similar-card__title">{s.title || 'Untitled'}</div>
                {#if s.artist}<div class="similar-card__artist">{s.artist}</div>{/if}
              </a>
            {/each}
          </div>
        </div>
      {/if}
    </div>
  {:else if error}
    <p class="empty">
      Something went wrong loading this track. <button class="link-btn" onclick={() => load()}>Try again</button>
    </p>
  {:else}
    <p class="empty">Track not found.</p>
  {/if}
</div>

<style>
  .narrow {
    max-width: 48rem;
    margin: 0 auto;
  }
  .track-detail {
    margin-top: 1rem;
  }
  .track-detail h1 {
    margin: 0;
    font-size: 1.25rem;
    flex: 1;
  }
  .title-row {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 0.5rem;
  }
  .stats {
    margin-top: 0.75rem;
    font-size: 0.875rem;
    color: var(--text-secondary);
    line-height: 1.6;
  }
  .ai-link {
    color: var(--green);
    font-weight: 600;
  }
  .empty {
    color: var(--text-secondary);
  }
  .link-btn {
    background: none;
    border: none;
    color: var(--cyan);
    cursor: pointer;
    padding: 0;
    font: inherit;
    text-decoration: underline;
  }

  /* Catalog-only attribution */
  .attribution {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-wrap: wrap;
    color: var(--text-secondary);
    font-size: 0.9rem;
  }
  .artist {
    color: var(--text);
    font-weight: 500;
  }
  .sep {
    color: var(--text-muted);
  }
  .album {
    color: var(--text-muted);
  }
  .source-badge {
    margin-left: 0.25rem;
    padding: 0.125rem 0.5rem;
    border-radius: 999px;
    font-size: 0.7rem;
    font-weight: 500;
    background: rgba(0, 229, 255, 0.15);
    color: var(--cyan);
  }
  .tier {
    padding: 0.125rem 0.5rem;
    border-radius: 999px;
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: capitalize;
    background: var(--surface-2);
    color: var(--text);
  }
  .tier-underground {
    background: rgba(255, 215, 0, 0.15);
    color: var(--gold);
  }
  .tier-mainstream {
    background: rgba(236, 72, 153, 0.15);
    color: var(--pink);
  }
  .back {
    margin-top: 1.5rem;
  }
  .back a {
    color: var(--cyan);
    font-size: 0.875rem;
  }

  /* Submit CTA */
  .voted-with {
    display: flex;
    align-items: center;
    gap: 0.65rem;
    margin: 0.5rem 0 0;
    font-size: 0.85rem;
    color: var(--text-muted);
  }
  .voted-with-avatars {
    display: flex;
  }
  .voted-with-avatar {
    display: inline-block;
    margin-left: -0.45rem;
    border: 2px solid var(--bg);
    border-radius: 50%;
    line-height: 0;
    transition: transform 0.15s ease;
  }
  .voted-with-avatar:first-child {
    margin-left: 0;
  }
  .voted-with-avatar:hover {
    transform: translateY(-1px);
    z-index: 1;
  }
  .voted-with-text :global(strong) {
    color: var(--text);
    font-weight: 600;
  }

  .submit-cta {
    margin-top: 1.25rem;
    padding: 1rem 1.25rem;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 0.5rem;
  }
  .submit-link {
    color: var(--pink);
    font-weight: 600;
    font-size: 0.875rem;
  }
  .browse-link {
    color: var(--cyan);
    font-size: 0.8125rem;
  }

  /* Voter Audience DNA */
  .voter-dna {
    margin-top: 1.5rem;
    padding: 1rem;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius);
  }
  .voter-dna-title {
    margin: 0 0 0.25rem;
    font-size: 0.85rem;
    text-transform: uppercase;
    letter-spacing: 1px;
    color: var(--pink);
  }
  .voter-dna-sub {
    margin: 0 0 0.75rem;
    font-size: 0.75rem;
    color: var(--text-muted);
  }
  .dna-bars {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
  }
  .dna-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  .dna-label {
    width: 100px;
    font-size: 0.75rem;
    color: var(--text-secondary);
    text-align: right;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .dna-bar-bg {
    flex: 1;
    height: 8px;
    background: rgba(255, 255, 255, 0.08);
    border-radius: 4px;
    overflow: hidden;
  }
  .dna-bar-fill {
    height: 100%;
    background: linear-gradient(90deg, var(--purple), var(--pink));
    border-radius: 4px;
    transition: width 0.3s;
  }
  .dna-pct {
    width: 40px;
    font-size: 0.7rem;
    color: var(--text-muted);
  }

  /* BeatDuel Effect */
  .legacy-section {
    margin-top: 1.5rem;
    padding: 1rem;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius);
  }
  .legacy-title {
    margin: 0 0 0.75rem;
    font-size: 0.85rem;
    text-transform: uppercase;
    letter-spacing: 1px;
    color: #10b981;
  }
  .legacy-stats {
    display: flex;
    gap: 2rem;
    flex-wrap: wrap;
  }
  .legacy-stat {
    display: flex;
    flex-direction: column;
  }
  .legacy-delta {
    font-size: 1.5rem;
    font-weight: 800;
    color: #10b981;
  }
  .legacy-label {
    font-size: 0.75rem;
    color: var(--text-secondary);
  }
  .legacy-sub {
    font-size: 0.7rem;
    color: var(--text-muted);
    margin-top: 0.5rem;
    display: block;
  }

  .track-attribution {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 0.5rem;
    flex-wrap: wrap;
  }
  .submitter-link {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    color: var(--text-secondary);
    font-size: 0.9rem;
  }
  .submitter-link:hover {
    color: var(--text);
  }
  .attr-sep {
    color: var(--text-muted);
  }
  .artist-link {
    color: var(--purple);
    font-weight: 500;
    font-size: 0.9rem;
  }
  .artist-link:hover {
    color: var(--pink);
  }

  .tag-pills {
    display: flex;
    flex-wrap: wrap;
    gap: 0.375rem;
    margin-top: 0.5rem;
    margin-bottom: 0.5rem;
  }
  .tag-pill {
    padding: 0.125rem 0.5rem;
    border-radius: 999px;
    font-size: 0.7rem;
    font-weight: 500;
    background: rgba(147, 51, 234, 0.12);
    color: var(--purple);
  }
  .tag-style {
    background: rgba(0, 229, 255, 0.12);
    color: var(--cyan);
  }

  .duel-context {
    margin-top: 0.75rem;
    padding: 0.75rem;
    background: var(--surface-2);
    border-radius: var(--radius);
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }
  .duel-link {
    color: var(--cyan);
    font-weight: 500;
  }
  .duel-link:hover {
    color: var(--text);
  }
  .duel-rank {
    font-size: 0.8rem;
    font-weight: 700;
    padding: 0.125rem 0.5rem;
    border-radius: 999px;
    background: var(--surface-3);
  }
  .rank-1 {
    background: rgba(255, 215, 0, 0.2);
    color: var(--gold);
  }
  .rank-2 {
    background: rgba(192, 192, 192, 0.2);
    color: #c0c0c0;
  }
  .rank-3 {
    background: rgba(205, 127, 50, 0.2);
    color: #cd7f32;
  }
  .duel-status-badge {
    font-size: 0.75rem;
    font-weight: 600;
    padding: 0.125rem 0.5rem;
    border-radius: 999px;
    background: rgba(0, 229, 255, 0.15);
    color: var(--cyan);
  }

  /* "Sounds like this" similar-tracks rail */
  .similar-rail {
    margin-top: 2rem;
    padding-top: 1.25rem;
    border-top: 1px solid var(--border);
  }
  .similar-rail__title {
    margin: 0 0 0.75rem;
    font-size: 0.85rem;
    text-transform: uppercase;
    letter-spacing: 1px;
    color: var(--text-secondary);
  }
  .similar-rail__track-row {
    display: flex;
    gap: 0.75rem;
    overflow-x: auto;
    padding-bottom: 0.5rem;
  }
  .similar-card {
    flex: 0 0 auto;
    width: 120px;
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
    text-decoration: none;
    color: var(--text);
  }
  .similar-card:hover .similar-card__title {
    color: var(--cyan);
  }
  .similar-card__art {
    width: 120px;
    height: 120px;
    object-fit: cover;
    border-radius: var(--radius);
    background: var(--surface-2);
    display: block;
  }
  .similar-card__title {
    font-size: 0.8rem;
    font-weight: 500;
    line-height: 1.3;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
  }
  .similar-card__artist {
    font-size: 0.7rem;
    color: var(--text-muted);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
</style>
